import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, useParams } from 'react-router';
import { HomePage } from './pages/HomePage';
import { BookingPage } from './pages/BookingPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { useI18n } from './lib/i18n';

/** Customer self-service is rarely needed; it loads only when visited. */
const ManagePage = lazy(() => import('./pages/ManagePage'));
const CustomerSignInPage = lazy(() => import('./pages/CustomerSignInPage'));
const CustomerHomePage = lazy(() => import('./pages/CustomerHomePage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const FormPage = lazy(() => import('./pages/FormPage'));
/** The published policies (0046): read by anybody, no session needed. */
const LegalPage = lazy(() => import('./pages/LegalPage'));
const IndustryLandingPage = lazy(() => import('./pages/IndustryLandingPage'));
const KioskPage = lazy(() => import('./pages/KioskPage'));
/** The contact page and a request opened by its emailed link (0058). */
const SupportPage = lazy(() => import('./pages/SupportPage'));

/**
 * First path segments the application owns and no business may take.
 *
 * THIS IS THE CLIENT'S COPY OF `reserved_slugs` (migrations 0002 and
 * 0027), which the database enforces with a trigger on every insert or
 * update of `organizations.slug`. It exists so a reserved word that has
 * no screen of its own (`/pricing`, `/privacy`, `/security`, …) answers
 * "nothing here" rather than "this business has not published its
 * booking page yet" — there is no business at that address and never
 * can be, so the second sentence is a lie. Keep the two lists together:
 * a word added there should be added here.
 */
export const RESERVED_PUBLIC_PATHS: ReadonlySet<string> = new Set([
  'admin', 'api', 'health', 'assets', 'login', 'signup', 'logout',
  'book', 'booking', 'app', 'static', 'public', 'privacy', 'terms',
  'about', 'help', 'support', 'status', 'manifest.webmanifest', 'robots.txt',
  'sitemap.xml', 'favicon.ico', 'favicon.svg', 'sw.js', 'workforceassemble', 'assemble',
  'invite', 'invitations', 'me', 'account', 'customer', 'customers',
  'appointments', 'sign-in', 'signin', 'sign-up', 'pay', 'payment',
  'payments', 'pricing', 'plans', 'features', 'continue', 'billing',
  'start', 'welcome', 'contact', 'legal', 'security', 'go', 'kiosk',
]);

/**
 * /:slug and /:slug/:pageSlug, unless the slug is a reserved word: then
 * it is a plain "page not found", because the business-not-found copy
 * would name a business that cannot exist.
 */
function BusinessRoute() {
  const { slug = '' } = useParams();
  const { t } = useI18n();
  if (RESERVED_PUBLIC_PATHS.has(slug)) return <NotFoundPage message={t('page.reserved_not_found_message')} />;
  return <BookingPage />;
}

function loading(what: string): ReactNode {
  return <main className="page"><p className="muted loading-note">{what}</p></main>;
}

function lazily(element: ReactNode, what: string): ReactNode {
  return <Suspense fallback={loading(what)}>{element}</Suspense>;
}

/**
 * Public routes: the entry page, each organization's booking page at
 * /:slug, the customer's own account, and invitations. Kept deliberately
 * small — this bundle is what a customer on a phone downloads, so every
 * screen beyond the first two is loaded only when it is opened.
 * Administration lives in a separate entry (admin.html).
 *
 * Every path here is in `reserved_slugs` (migration 0027), so no
 * business can take a name that would shadow one of these screens.
 */
export const router = createBrowserRouter([
  { path: '/', element: <HomePage /> },
  { path: '/sign-in', element: lazily(<CustomerSignInPage />, 'Loading…') },
  { path: '/me', element: lazily(<CustomerHomePage />, 'Loading your appointments…') },
  { path: '/invite/:token', element: lazily(<InvitePage />, 'Checking your invitation…') },
  // The policies, registered BEFORE /:slug so 'legal' is never read as a
  // business. 'legal' is in reserved_slugs (0027) and in
  // RESERVED_PUBLIC_PATHS above, so no business can take it either way.
  { path: '/legal', element: lazily(<LegalPage />, 'Loading…') },
  { path: '/legal/:slug', element: lazily(<LegalPage />, 'Loading…') },
  // Advertising landing pages live under the already-reserved /features
  // namespace, so no business slug or migration is needed for each trade.
  { path: '/features/:industry', element: lazily(<IndustryLandingPage />, 'Loading…') },
  // The kiosk at the counter (0056). Before /:slug, or 'kiosk' would
  // be read as a business.
  { path: '/kiosk/:slug', element: lazily(<KioskPage />, 'Loading…') },
  // Contact and support (0058). 'support' is reserved, so before /:slug.
  { path: '/support', element: lazily(<SupportPage />, 'Loading…') },
  { path: '/support/ticket/:id', element: lazily(<SupportPage />, 'Opening your request…') },
  { path: '/:slug', element: <BusinessRoute /> },
  // The shop (0040). Registered BEFORE /:slug/:pageSlug and reserved
  // against industry page slugs in the database, so an ordering page can
  // never be shadowed by a page somebody named "shop".
  { path: '/:slug/shop', element: lazily(<ShopPage />, 'Loading the shop…') },
  // One business, several trades: an industry page is the same booking
  // engine and the same calendar behind a different introduction.
  { path: '/:slug/:pageSlug', element: <BusinessRoute /> },
  { path: '/:slug/appointments/:id', element: lazily(<ManagePage />, 'Loading your appointment…') },
  // An intake form, opened by the customer's own secure link (0041).
  // Registered before /:slug/:pageSlug would be reached for three
  // segments, and 'forms' is reserved against industry page slugs in the
  // database exactly as 'shop' and 'appointments' are.
  { path: '/:slug/forms/:token', element: lazily(<FormPage />, 'Loading your form…') },
  { path: '*', element: <NotFoundPage /> },
]);
